/**
 * Time string parsing utilities for handling various time formats
 * Converts between ISO timestamps, formatted time strings, and TimeFields objects
 */

type TimeFields = {
  hour: string
  minute: string
  ampm: string
}

/**
 * Parse ISO timestamp (e.g., "2026-06-26T01:00:00.000Z") into time string ("1:00 AM")
 */
export const parseISOTimeString = (isoTime: string): string | null => {
  if (!isoTime) return null
  try {
    const date = new Date(isoTime)
    let hours = date.getUTCHours()
    const minutes = String(date.getUTCMinutes()).padStart(2, '0')
    const ampm = hours >= 12 ? 'PM' : 'AM'
    hours = hours % 12 || 12
    return `${hours}:${minutes} ${ampm}`
  } catch {
    return null
  }
}

/**
 * Parse time string into from/to time field objects
 * Handles both formatted ("1:00 AM - 2:00 PM") and ISO combined ("2026-06-26T01:00:00.000Z - 2026-06-26T02:00:00.000Z") formats
 */
export const parseTimeString = (
  timeStr: string
): { from: TimeFields; to: TimeFields } | null => {
  if (!timeStr || timeStr === 'Full Day') return null

  // Check if timeStr contains ISO format timestamps
  if (timeStr.includes('T') && timeStr.includes('Z')) {
    // Extract the two ISO timestamps
    const match =
      /(\d{4}-\d{2}-\d{2}T[\d:.]+Z)\s-\s(\d{4}-\d{2}-\d{2}T[\d:.]+Z)/.exec(
        timeStr
      )
    if (match) {
      const startTimeStr = parseISOTimeString(match[1])
      const endTimeStr = parseISOTimeString(match[2])
      if (startTimeStr && endTimeStr) {
        const parsedMatch =
          /(\d{1,2}):(\d{2})\s(AM|PM)\s-\s(\d{1,2}):(\d{2})\s(AM|PM)/.exec(
            `${startTimeStr} - ${endTimeStr}`
          )
        if (parsedMatch) {
          return {
            from: {
              hour: parsedMatch[1],
              minute: parsedMatch[2],
              ampm: parsedMatch[3],
            },
            to: {
              hour: parsedMatch[4],
              minute: parsedMatch[5],
              ampm: parsedMatch[6],
            },
          }
        }
      }
    }
    return null
  }

  // Match format: "H:MM AM - H:MM PM" or "HH:MM AM - HH:MM PM"
  const match =
    /(\d{1,2}):(\d{2})\s(AM|PM)\s-\s(\d{1,2}):(\d{2})\s(AM|PM)/.exec(timeStr)
  if (!match) return null

  return {
    from: { hour: match[1], minute: match[2], ampm: match[3] },
    to: { hour: match[4], minute: match[5], ampm: match[6] },
  }
}

/**
 * Convert 24-hour format time range to 12-hour AM/PM format
 * Input: "01:00 - 15:00" → Output: "1:00 AM - 3:00 PM"
 * Input: "9:00 - 13:00" → Output: "9:00 AM - 1:00 PM"
 */
export const convertTo12HourFormat = (timeStr: string): string => {
  if (!timeStr || timeStr === 'Full Day') return timeStr

  // Already in 12-hour format with AM/PM
  if (timeStr.includes('AM') || timeStr.includes('PM')) {
    return timeStr
  }

  // Match 24-hour format: "HH:MM - HH:MM" or "H:MM - H:MM"
  const match = /(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/.exec(timeStr)
  if (!match) return timeStr

  const convertHour = (hour: number): { hour: number; period: string } => {
    if (hour === 0) return { hour: 12, period: 'AM' }
    if (hour < 12) return { hour, period: 'AM' }
    if (hour === 12) return { hour: 12, period: 'PM' }
    return { hour: hour - 12, period: 'PM' }
  }

  const startHour = Number.parseInt(match[1], 10)
  const startMin = match[2]
  const endHour = Number.parseInt(match[3], 10)
  const endMin = match[4]

  const start = convertHour(startHour)
  const end = convertHour(endHour)

  return `${start.hour}:${startMin} ${start.period} - ${end.hour}:${endMin} ${end.period}`
}
